import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    PatientOverviewV2TimelineCardComponent
} from "@cdss/pages/patient-overview-v2/timeline/card/patient-overview-v2-timeline-card.component";
import {PatientStatisticsService} from "@shared/api/services/v2/patient-statistics.service";
import {UserCareProgramWeek} from "@shared/api/models/query/user-care-program.model";
import {TimelineData} from "@shared/api/models/query/patient-statistics.model";
import {OverlaySpinnerComponent} from "@shared/components/overlay-spinner/overlay-spinner.component";

/** Pointer travel (px) before a press on the timeline counts as a drag rather than a click. */
const TimelineDragThresholdPx = 5;

@Component({
    selector: 'cdss-patient-overview-v2-timeline',
    imports: [CommonModule, PatientOverviewV2TimelineCardComponent, OverlaySpinnerComponent],
    templateUrl: './patient-overview-v2-timeline.component.html',
    standalone: true,
    styleUrl: './patient-overview-v2-timeline.component.scss'
})
export class PatientOverviewV2TimelineComponent {

    private isDragging = false;
    private startX = 0;
    private scrollLeft = 0;

    @Input() type: "patient" | "carer" = 'patient';
    @Input() patientId: number | string = null;
    private _currentWeek: UserCareProgramWeek = null
    disabled: boolean = false
    loading: boolean = false

    // v2 statistics contract (UNIVAPLAT-2359 / UNIVAPLAT-2376).
    timeline: TimelineData = null
    days: Date[] = []

    constructor(
        private patientStatisticsService: PatientStatisticsService
    ) {
    }

    @Input()
    set currentWeek(week: UserCareProgramWeek) {
        if (!week) return
        this._currentWeek = week
        this.refresh()
        this.days = this.getDaysBetween(new Date(week.dateStart), new Date(week.dateEnd))
    }
    get currentWeek(): UserCareProgramWeek {
        return this._currentWeek
    }

    getDaysBetween(start: Date, end: Date): Date[] {
        const days =
            Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        return Array.from({ length: days }, (_, i) => {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            return date;
        });
    }

    async refresh() {
        this.loading = true
        this.disabled = true

        if (this.type == "patient") {
            this.timeline = await this.patientStatisticsService.getPatientTimeline(
                this._currentWeek.patientId,
                this._currentWeek.dateStart,
                this._currentWeek.dateEnd,
            )
        }

        if (this.type == "carer") {
            this.timeline = await this.patientStatisticsService.getTimeline(
                this._currentWeek.patientId,
                this._currentWeek.dateStart,
                this._currentWeek.dateEnd,
                "carer"
            )
        }
        this.loading = false
        this.disabled = false
    }

    startDrag(event: PointerEvent) {
        this.isDragging = true;

        const target = event.currentTarget as HTMLElement;

        this.startX = event.pageX - target.offsetLeft;
        this.scrollLeft = target.scrollLeft;
    }

    onDrag(event: PointerEvent) {
        if (!this.isDragging) return;

        const target = event.currentTarget as HTMLElement;

        const x = event.pageX - target.offsetLeft;
        const walk = (x - this.startX) * 1; // velocidad

        // Pointer capture keeps this element receiving pointermove/pointerup
        // even once the cursor travels outside its bounds mid-drag — plain
        // mousemove/mouseleave would end the drag the instant that happens,
        // which is why fast or slightly-off-course drags previously did
        // nothing beyond the first pixel of movement. It is only taken once
        // the pointer has actually moved: capturing on pointerdown retargets
        // the click to this container, so the cards' "daily detail" arrows
        // never received their click.
        if (!target.hasPointerCapture(event.pointerId)) {
            if (Math.abs(walk) < TimelineDragThresholdPx) return;
            target.setPointerCapture(event.pointerId);
        }

        event.preventDefault();

        target.scrollLeft = this.scrollLeft - walk;
    }

    endDrag(event: PointerEvent) {
        this.isDragging = false;

        const target = event.currentTarget as HTMLElement;
        if (target.hasPointerCapture(event.pointerId)) {
            target.releasePointerCapture(event.pointerId);
        }
    }
}
